import isEmpty from 'lodash/isEmpty';
import type { UseFormReturn } from 'react-hook-form';
import type { ConfigTypeParameters, MerchantDataPayload } from '@repo/api/endpoints/merchant';

import { companyType, LoanFormInputFields } from '@/components/loan-form/schema/loan-form.schema';
import { RenderDetailByType } from '@/components/loan-form/components/render-detail-by-type';
import { getPlatformUrl } from '@/components/loan-form/helpers/get-platform-url';
import { getSettingsUrl } from '@/components/loan-form/helpers/get-settings-url';
import { isValidIdentityDocument } from '@/components/loan-form/helpers/is-valid-identity-document';

import { DetailWrapper } from './detail-wrapper';

interface FieldProps {
  parameter: ConfigTypeParameters[number];
  index: number;
  data: MerchantDataPayload;
  formContext: UseFormReturn<LoanFormInputFields>;
}
export function SingleRecipientDetail({
  data,
  formContext,
  index,
  parameter: { name },
}: FieldProps) {
  const fieldValue = formContext.getValues(name);
  const isEven = index % 2 !== 0;

  const bankAccountValues = data.merchantData.bankAccount.filter((account) => account.iban);

  const isBankAccountMultipleChoice = name === 'bankAccount' && bankAccountValues.length > 1;

  const isRepresentativeMultipleChoice =
    name === 'name_surname' && data.merchantData.representative.length > 1;

  const isInvalidDocumentType = () => {
    const selectedCompanyType = companyType(data.merchantData.companyType);
    if (selectedCompanyType === 'jednoosobowa') {
      const [documentType, documentCountry] = formContext.getValues([
        'documentType',
        'documentCountry',
      ]);
      if (!isValidIdentityDocument(documentType, documentCountry)) {
        return false;
      }
    }

    return true;
  };
  const isPhoneFieldEmpty = name === 'phone' && isEmpty(fieldValue);
  const isMailFieldEmpty = name === 'mail' && isEmpty(fieldValue);
  const isDocumentFieldEmpty = name === 'documentId' && isEmpty(fieldValue);
  const isDocumentTypeError = name === 'documentId' && !isInvalidDocumentType();

  const platformUrl = getPlatformUrl();
  const settingsUrl = getSettingsUrl(data.merchantData, platformUrl);

  const detailType = () => {
    if (isBankAccountMultipleChoice) {
      const bankAccountsOptions = data.merchantData.bankAccount.map((elem, id) => {
        return { id, name: elem.iban, value: elem.id };
      });

      return {
        select: {
          name,
          options: bankAccountsOptions,
          defaultValue: bankAccountsOptions[0].value,
        },
      };
    }

    if (isRepresentativeMultipleChoice) {
      const representativeFullNameOptions = data.merchantData.representative.map((elem, id) => {
        return {
          id,
          name: `${elem.name} ${elem.surname}`,
          value: elem.id,
        };
      });

      return {
        select: {
          name,
          options: representativeFullNameOptions,
          defaultValue: representativeFullNameOptions[0].value,
        },
      };
    }

    if (isPhoneFieldEmpty || isMailFieldEmpty || isDocumentFieldEmpty || isDocumentTypeError) {
      return {
        link: {
          name: isDocumentTypeError ? ('documentType' as const) : name,
          href: settingsUrl,
        },
      };
    }

    const isSingleRepresentative =
      name === 'name_surname' && data.merchantData.representative.length === 1;
    const isSingleBankAccount =
      name === 'bankAccount' && data.merchantData.bankAccount.length === 1;

    if (isSingleRepresentative || isSingleBankAccount) {
      return {
        text: {
          name,
          value: isSingleRepresentative
            ? `${data.merchantData.representative[0].name} ${data.merchantData.representative[0].surname}`
            : data.merchantData.bankAccount[0].iban,
        },
      };
    }

    return {
      text: { name, value: fieldValue },
    };
  };

  return (
    <DetailWrapper configParameterName={name} isEven={isEven}>
      <RenderDetailByType detailType={detailType()} />
    </DetailWrapper>
  );
}
